import React, { useMemo, useEffect } from 'react';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useActiveRightSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/constants';
import {
	type RovoEntryPointComponent,
	useAiRovoActiveConfiguration,
} from './useAiRovoActiveConfiguration.tsx';

const entryPointParams = {};

export const AiRovoCurrentRouteConfigurationProvider = (): React.JSX.Element | null => {
	const { resetConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const configuration = useAiRovoActiveConfiguration();

	useEffect(() => {
		if (!configuration?.entrypoint) {
			resetConfiguration();
		}
	}, [resetConfiguration, configuration]);

	if (!configuration) {
		return null;
	}

	return (
		<>
			<RovoEntryPointContainer entrypoint={configuration.entrypoint} />
			{configuration.nudges && configuration.nudges()}
		</>
	);
};

const RovoEntryPointContainer = ({ entrypoint }: { entrypoint: RovoEntryPointComponent }) => {
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entrypoint,
		entryPointParams,
	);
	const { setConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const runtimeProps = useMemo(() => ({ setConfiguration }), [setConfiguration]);
	const { mode } = useRovoExperienceAPIStore();

	// TODO this shouldn't be needed since mode should be set to 'sidebar' when the sidebar is open,
	// but it isn't working right now. We should fix this in the rovo experience api store.
	const { panelId, isCollapsed } = useActiveRightSidebarState() || {};
	const isActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID && !isCollapsed;

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (mode === 'mini-modal' || mode === 'sidebar' || isActive) {
			entryPointActions.load();
		}
	}, [entryPointActions, mode, isActive]);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="jwm.rovo.current-route-configuration"
			packageName="jiraAiRovoActionsRouteProvider"
			runtimeProps={runtimeProps}
		/>
	);
};
